import {RoomClientState} from '../../RoomClientState.ts'
import {LobbyImage, DiceSettingEnum} from '@colonist/types'
import {UIComponentItemScroll} from '../../ui_components/item_scroll/UIComponentItemScroll'
import {ItemData} from '../../ui_components/item_scroll/UIComponentItemScrollCell'
import {notificationController, socket} from '../../EntryShared'
import {ViewPopupRulesInformation} from '../../ui_popup/ViewPopupRulesInformation'
import {UITopNotificationType} from '../../notifications/UINotificationController'
import {TranslationObject} from 'packages/i18n/src/types'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {StorageWrapper} from '../../StorageWrapper'
import {setElementOnClick} from '../../ui_utils/DomUtils'

export enum Rule {
    PrivateRoom,
    HideBankCards,
    FriendlyRobber,
    BalancedDice,
    NoTrolls,
}

export interface RuleData {
    available?: boolean
    selected?: boolean
    onClick: () => void
    onInfoClick?: () => void
}

interface RuleItemData extends ItemData {
    visible: boolean
}

export class UIRoomSettingsRulesController {
    private readonly container: HTMLDivElement
    private rulesSelector: UIComponentItemScroll

    private roomState: RoomClientState
    private rulesData: Map<Rule, RuleData>

    constructor(container: HTMLDivElement) {
        this.container = container
    }

    load(roomState: RoomClientState, rulesData?: Map<Rule, RuleData>) {
        this.roomState = roomState
        this.rulesData = rulesData ?? new Map<Rule, RuleData>()

        const infoIcon = document.getElementById('room_center_rules_info_icon') as HTMLImageElement
        const isTournamentRoom = this.roomState.isTournament
        if(infoIcon != undefined) {
            setElementOnClick(infoIcon, () => {
                new ViewPopupRulesInformation(isTournamentRoom)
            })
        }

        if(this.rulesSelector == undefined) {
            this.createRulesSelector()
            return
        }
        this.updateRulesSelector()
    }

    update(newRoomState: RoomClientState, rulesData?: Map<Rule, RuleData>) {
        const shouldUpdate = this.shouldUpdate(newRoomState)

        this.roomState = newRoomState
        if(rulesData) this.rulesData = rulesData

        if(shouldUpdate) this.updateRulesSelector()
    }

    updateInteractabilityOfElements(isGuest: boolean) {
        this.rulesSelector.updateInteractabilityOfElements(isGuest)
    }

    overrideRuleAvailableState(rule: Rule, available: boolean) {
        const ruleData = this.rulesData.get(rule)
        if(ruleData == undefined) return
        ruleData.available = available
        this.updateRulesSelector()
    }

    overrideRuleSelectedState(rule: Rule, selected: boolean) {
        const ruleData = this.rulesData.get(rule)
        if(ruleData == undefined) return
        ruleData.selected = selected
        this.updateRulesSelector()
    }

    private createRulesSelector() {
        const rulesItemData = this.getRulesItemData()
        this.rulesSelector = new UIComponentItemScroll(this.container, rulesItemData, false)
    }

    private updateRulesSelector() {
        const rulesItemData = this.getRulesItemData()
        this.rulesSelector.update(rulesItemData)
    }

    private getRulesItemData(): RuleItemData[] {
        const rulesItemData: RuleItemData[] = [
            {
                name: {key: 'strings:roomPage.options.privateGame.title'},
                image: LobbyImage.PrivateGame,
                available: true,
                selected: this.rulesData.get(Rule.PrivateRoom)?.selected ?? this.roomState.gameSetting.privateGame,
                canToggle: true,
                clickAction: () => this.onClickPrivateGame(),
                visible: !this.isTournamentRoom(),
            },
            {
                name: {key: 'strings:roomPage.options.hideBankCards.title'},
                image: LobbyImage.HideBankCards,
                available: true,
                selected: this.roomState.gameSetting.hideBankCards,
                canToggle: true,
                clickAction: () => this.onClickHideBankCards(),
                visible: true,
            },
            {
                name: {key: 'strings:roomPage.options.friendlyRobber.title'},
                image: LobbyImage.Robber,
                available: true,
                selected: this.roomState.gameSetting.friendlyRobber,
                canToggle: true,
                clickAction: () => this.onClickFriendlyRobber(),
                visible: true,
            },
            {
                name: {key: 'strings:roomPage.options.dice.titleMobile'},
                image: LobbyImage.Dice6,
                available: true,
                selected: this.roomState.gameSetting.diceSetting == DiceSettingEnum.Balanced,
                canToggle: true,
                clickAction: () => this.onClickBalancedDice(),
                visible: true,
            },
            {
                name: {key: 'strings:roomPage.options.noTrollsGame.title'},
                image: LobbyImage.IconNoTroll,
                available: this.rulesData.get(Rule.NoTrolls)?.available ?? !this.isNoTrollsDisabled(),
                selected: this.rulesData.get(Rule.NoTrolls)?.selected ?? this.roomState.isNoTrollsGame(),
                canToggle: true,
                clickAction: () => this.onClickNoTrolls(),
                infoAction: () => this.sendNoTrollsNotification(),
                visible: !this.isTournamentRoom(),
            },
            {
                name: {key: 'strings:roomPage.orderSelection.title'},
                image: LobbyImage.ProfileRanked,
                available: true,
                selected: this.roomState.gameSetting.playOrderSelectionActive,
                canToggle: true,
                clickAction: () => socket.roomClient.togglePlayOrderSelection(),
                visible: this.isTournamentRoom(),
            },
        ]

        return rulesItemData.filter((rule) => rule.visible)
    }

    private onClickPrivateGame(): void {
        const onClick = this.rulesData.get(Rule.PrivateRoom)?.onClick
        if(onClick != undefined) {
            onClick()
            return
        }
        socket.roomClient.setPrivateGame(!this.roomState.gameSetting.privateGame)
    }

    private onClickHideBankCards(): void {
        socket.roomClient.setHideBankCards(!this.roomState.gameSetting.hideBankCards)
    }

    private onClickFriendlyRobber(): void {
        const isCurrentlyFriendlyRobber = this.roomState.gameSetting.friendlyRobber
        socket.roomClient.setFriendlyRobber(!isCurrentlyFriendlyRobber)
        StorageWrapper.setRoomFriendlyRobberActiveSetting(!isCurrentlyFriendlyRobber)
    }

    private onClickBalancedDice(): void {
        const isCurrentlyBalanced = this.roomState.gameSetting.diceSetting == DiceSettingEnum.Balanced
        const newSetting = isCurrentlyBalanced ? DiceSettingEnum.Random : DiceSettingEnum.Balanced
        socket.roomClient.setDiceType(newSetting)
        StorageWrapper.setRoomDiceSetting(newSetting)
    }

    private onClickNoTrolls(): void {
        const onClick = this.rulesData.get(Rule.NoTrolls)?.onClick
        if(onClick != undefined) {
            onClick()
            return
        }
        socket.roomClient.toggleNoTrolls()
    }

    private shouldUpdate(newRoomState: RoomClientState): boolean {
        if(this.roomState.hostSession.userId != newRoomState.hostSession.userId) return true
        if(this.roomState.gameSetting.privateGame != newRoomState.gameSetting.privateGame) return true
        if(this.roomState.gameSetting.hideBankCards != newRoomState.gameSetting.hideBankCards) return true
        if(this.roomState.gameSetting.friendlyRobber != newRoomState.gameSetting.friendlyRobber) return true
        if(this.roomState.gameSetting.diceSetting != newRoomState.gameSetting.diceSetting) return true
        if(this.roomState.isNoTrollsGame() != newRoomState.isNoTrollsGame()) return true
        if(this.roomState.gameSetting.playOrderSelectionActive != newRoomState.gameSetting.playOrderSelectionActive) return true

        return false
    }

    private isTournamentRoom(): boolean {
        if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return false
        return this.roomState.isTournament
    }

    private isNoTrollsDisabled(): boolean {
        if(!this.roomState.isHostSubscribed()) return true
        if(this.roomState.isPrivateGame()) return true
        return false
    }

    private sendNoTrollsNotification(): void {
        const onInfoClick = this.rulesData.get(Rule.NoTrolls)?.onInfoClick
        if(onInfoClick != undefined) {
            onInfoClick()
            return
        }

        const message: TranslationObject = this.roomState.gameSetting.privateGame ? {key: 'strings:roomPage.notifications.noTrollsGameDisabledWhenPrivate'} : {key: 'strings:roomPage.options.noTrollsGame.becomeAMember'}

        if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) {
            notificationController.show(message, UITopNotificationType.Information, undefined, undefined, undefined, undefined, 3000)
            return
        }

        notificationController.showNotification(LobbyImage.Lightning, message)
    }
}
